import { Injectable } from '@angular/core';
import { Solution, SolutionProperty, SolutionPropertyConfig, SolutionResponse } from '../models/solutions.types';
import { PropertyValueWithUnit } from '../../../core/models/abacus-api.types';

@Injectable({ providedIn: 'root' })
export class SolutionMapper {
  private readonly propertyRegistry = new Map<keyof SolutionResponse, SolutionPropertyConfig>([
    // Diesel
    ['PSC_Diesel_Cost_Per_liter__c', { label: 'Diesel cost per liter' }],
    ['PSC_Diesel_Cost__c', { label: 'Diesel cost' }],
    ['PSC_Diesel_Liter__c', { label: 'Diesel consumption' }],
    ['PSC_Diesel_kgCO2e__c', { label: 'Diesel CO₂ emissions' }],

    // Electricity
    ['PSC_Electricity_KWh__c', { label: 'Electricity consumption' }],
    ['PSC_Electricity_Cost__c', { label: 'Electricity cost' }],
    ['PSC_Electricity_kgCO2e__c', { label: 'Electricity CO₂ emissions' }],
    ['PSC_Electricity_Cost_EUR__c', { label: 'Electricity cost (EUR)' }],

    // Natural Gas
    ['PSC_Natural_Gas__c', { label: 'Natural gas consumption' }],
    ['PSC_Natural_Gas_Cost__c', { label: 'Natural gas cost' }],
    ['PSC_Natural_Gas_kgCO2e__c', { label: 'Natural gas CO₂ emissions' }],
    ['natural_gas_cost_liter', { label: 'Natural gas cost per liter' }],
    ['natural_gas_maximum_net_power', { label: 'Natural gas max power' }],

    // Emissions
    ['PSC_Emissions_Total_kgCO2e__c', { label: 'Total CO₂ emissions' }],
    ['PSC_Emissions_Cost_of_Carbon__c', { label: 'Carbon cost' }],
    ['PSC_Emissions_kg_Nox__c', { label: 'NOx emissions' }],
    ['PSC_Emissions_kg_PM__c', { label: 'PM emissions' }],

    // Totals
    ['PSC_Total_Energy__c', { label: 'Total energy' }],
    ['PSC_Total_TCU__c', { label: 'Total TCU' }],
  ]);

  private num(value: unknown): number | undefined {
    if (value === null || value === undefined) {
      return undefined;
    }
    if (this.isPropertyValueWithUnit(value)) {
      return value.value ?? undefined;
    }
    if (typeof value === 'number') {
      return value;
    }
    return undefined;
  }

  private unit(value: unknown): string | undefined {
    return this.isPropertyValueWithUnit(value) ? (value.unit ?? undefined) : undefined;
  }

  mapToSolution(response: SolutionResponse, index = 0): Solution {
    const rentalFee = this.num(response.PSC_Total_Rental_Fee__c) ?? 0;

    const dieselCost = this.num(response.PSC_Diesel_Cost__c) ?? 0;
    const electricityCost = this.num(response.PSC_Electricity_Cost__c) ?? 0;
    const naturalGasCost = this.num(response.PSC_Natural_Gas_Cost__c) ?? 0;
    const energyCost = dieselCost + electricityCost + naturalGasCost;

    return {
      id: response.Id,
      name: response.PSC_Solution_Name__c?.trim() ? response.PSC_Solution_Name__c : `Solution ${index + 1}`,
      isGreener: response.PSC_IsGreenerSolution__c ?? false,
      dieselType: response.PSC_Diesel_Type__c?.replace(/_/g, ' ') ?? null,
      economizer: response.PSC_Economizer__c ?? null,
      description: response.PSC_Solution_Description__c ?? null,
      properties: this.extractProperties(response),
      kpis: {
        rentalFeeTotal: rentalFee,
        energyCostTotal: energyCost,
        co2TotalKg: this.num(response.PSC_Emissions_Total_kgCO2e__c),
        noxKg: this.num(response.PSC_Emissions_kg_Nox__c),
        pmKg: this.num(response.PSC_Emissions_kg_PM__c),
        currencyUnit:
          this.unit(response.PSC_Total_Rental_Fee__c) ??
          this.unit(response.PSC_Diesel_Cost__c) ??
          this.unit(response.PSC_Electricity_Cost__c) ??
          this.unit(response.PSC_Natural_Gas_Cost__c),
        emissionUnit: this.unit(response.PSC_Emissions_Total_kgCO2e__c),
        pollutantUnit: this.unit(response.PSC_Emissions_kg_Nox__c) ?? this.unit(response.PSC_Emissions_kg_PM__c),
      },
    };
  }

  mapToSolutions(responses: SolutionResponse[]): Solution[] {
    return responses.map((r, i) => this.mapToSolution(r, i));
  }

  private extractProperties(response: SolutionResponse): SolutionProperty[] {
    const properties: SolutionProperty[] = [];

    for (const [key, config] of this.propertyRegistry) {
      const rawValue = response[key];
      const property = this.normalizeValue(rawValue, config);
      if (property) {
        properties.push(property);
      }
    }

    return properties;
  }

  private normalizeValue(value: unknown, config: SolutionPropertyConfig): SolutionProperty | null {
    if (value === null || value === undefined) {
      return null;
    }

    if (this.isPropertyValueWithUnit(value)) {
      if (value.value === null) {
        return null;
      }
      return {
        label: config.label,
        value: value.value,
        unit: value.unit ?? undefined,
      };
    }

    if (typeof value === 'string') {
      return {
        label: config.label,
        value: value.replace(/_/g, ' '),
      };
    }

    if (typeof value === 'number') {
      return {
        label: config.label,
        value,
      };
    }

    return null;
  }

  private isPropertyValueWithUnit(value: unknown): value is PropertyValueWithUnit<number | null, string | null> {
    return typeof value === 'object' && value !== null && 'value' in value && 'unit' in value;
  }
}
